import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  model,
  signal,
} from '@angular/core';
import { CasesService } from '@features/games/cases/services/cases.service';
import { SvgIconComponent } from 'angular-svg-icon';
import { SearchInputComponent } from '@core/components/search-input/search-input.component';
import { SelectComponent } from '@core/components/select/select.component';
import { CbCaseComponent } from '../cb-case/cb-case.component';
import { GameIconComponent } from '@core/components/game-icon/game-icon.component';
import { ICaseWithQuantity } from '@core/interfaces/cases.interface';
import { BalanceIconComponent } from '@core/components/balance-icon/balance-icon.component';
import { SessionService } from '@core/services/session.service';
import { DecimalPipe } from '@angular/common';
import { sleep } from '@core/utils/sleep.util';
import { CaseBattlesCreateService } from '../services/case-battles-create.service';
import { ModalService } from '@core/modals/services/modal.service';

enum SortOptions {
  highest_price,
  lowest_price,
}

interface SortOption {
  label: string;
  key: keyof typeof SortOptions;
  fn: () => void;
}

@Component({
  selector: 'app-cb-case-selection-modal',
  imports: [
    SvgIconComponent,
    SearchInputComponent,
    SelectComponent,
    CbCaseComponent,
    GameIconComponent,
    BalanceIconComponent,
    DecimalPipe,
  ],
  templateUrl: './cb-case-selection-modal.component.html',
  styleUrl: './cb-case-selection-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  host: {
    class: 'modal-container flex flex-col',
  },
})
export class CaseBattlesCaseSelectionModalComponent {
  public casesService = inject(CasesService);
  public caseBattlesCreateService = inject(CaseBattlesCreateService);
  public sessionService = inject(SessionService);
  public modalService = inject(ModalService);

  readonly search = signal<string>('');

  filtered = computed<ICaseWithQuantity[]>(() => {
    return this.casesService
      .casesList()
      .map((item) => {
        return {
          ...item,
          quantity: 0,
        };
      })
      .filter((item) =>
        item.name.toLowerCase().includes(this.search().toLowerCase())
      )
      .map((item) => {
        const selectedItem = this.caseBattlesCreateService
          .selectedCases()
          .find((caseItem) => caseItem.link === item.link);
        if (selectedItem) {
          return {
            ...selectedItem,
          };
        }
        return item;
      })
      .sort((a, b) => {
        return this.sort().key === 'highest_price'
          ? b.price - a.price
          : a.price - b.price;
      });
  });

  readonly sortOptions: SortOption[] = [
    {
      label: 'Highest Price',
      key: 'highest_price',
      fn: () => {
        this.sort.set(this.sortOptions[0]);
      },
    },
    {
      label: 'Lowest Price',
      key: 'lowest_price',
      fn: () => {
        this.sort.set(this.sortOptions[1]);
      },
    },
  ];
  readonly sort = signal<SortOption>(this.sortOptions[0]);

  isShowRefreshAnimation = signal(false);


  public handleIncreaseQuantity(caseItem: ICaseWithQuantity) {
    this.caseBattlesCreateService.increaseCaseQuantity(caseItem);
  }

  public handleDecreaseQuantity(caseItem: ICaseWithQuantity, remove?: boolean) {
    this.caseBattlesCreateService.decreaseCaseQuantity(caseItem, remove);
  }

  public async handleResetSelectedCases() {
    this.isShowRefreshAnimation.set(true);
    this.caseBattlesCreateService.resetSelectedCases();
    await sleep(500);
    this.isShowRefreshAnimation.set(false);
  }
}
